import React, { useState, useEffect } from "react";
import { Modal } from "../../components/ui/modal";
import Input from "../../components/form/input/InputField";
import ListboxComponent from "../../components/common/ListboxComponent";
import axios from "axios";
import { CheckCircle, CalendarDays } from "lucide-react";
import InputMask from "react-input-mask";
import {
  modalStyles,
  modalTitle,
  modalDescription,
  formStyles,
  label,
  input,
  errorMessage,
  closeButton,
  saveButton,
  saveButtonDisabled,
  successModalStyles,
  successModalCloseButton,
  successModalIconContainer,
  successModalHeading,
  successModalButton,
} from "../../styles/components/UserProfile/UserInfoCard.styles";

type CallModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCallAdded?: () => void;
  state: {
    lead_id: any;
  };
};

const CallModal: React.FC<CallModalProps> = ({
  isOpen,
  onClose,
  onCallAdded,
  state,
}) => {
  const { lead_id } = state;

  const [subject, setSubject] = useState("");
  const [call_date, setCallDate] = useState("");
  const [duration, setDuration] = useState("");
  const [status1, setStatus1] = useState("");
  const [status2, setStatus2] = useState("");
  const [call_description, setCallDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [assigned_to, setAssignedTo] = useState("");
  const [errors, setErrors] = useState<{
    subject?: string;
    call_date?: string;
    duration?: string;
    status1?: string;
    status2?: string;
    call_description?: string;
    assigned_to?: string;
  }>({});
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const status1Options = [
    { id: "Inbound", name: "Inbound" },
    { id: "Outbound", name: "Outbound" },
  ];

  const status2Options = [
    { id: "Planned", name: "Planned" },
    { id: "Held", name: "Held" },
    { id: "Not Held", name: "Not Held" },
  ];

  const token = sessionStorage.getItem("access_token");
  const userRoles = JSON.parse(sessionStorage.getItem("user_roles") || "[]");
  const isAdmin = userRoles.includes("Admin");

  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + now.getTimezoneOffset() + 330); // Adjust to IST
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/name/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();
        if (data.status === 200) {
          setUsers(data.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setErrors((prev) => ({ ...prev, assigned_to: "Error loading users." }));
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setCallDate("");
      setDuration("");
      setStatus1("");
      setStatus2("");
      setCallDescription("");
      setAssignedTo("");
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof errors
  ) => {
    const value = e.target.value;
    switch (field) {
      case "subject":
        setSubject(value);
        break;
      case "call_date":
        setCallDate(value);
        break;
      case "duration":
        setDuration(value);
        break;
      case "call_description":
        setCallDescription(value);
        break;
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDropdownChange = (field: keyof typeof errors) => (
    selected: { id: string; name: string } | null
  ) => {
    const value = selected?.id || "";
    switch (field) {
      case "status1":
        setStatus1(value);
        break;
      case "status2":
        setStatus2(value);
        break;
      case "assigned_to":
        setAssignedTo(value);
        break;
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateDuration = (value: string) => {
    if (!value) return "Duration is required";
    const regex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (!regex.test(value)) return "Invalid duration format (HH:mm:ss)";
    const [hours, minutes, seconds] = value.split(":").map(Number);
    if (hours > 23 || minutes > 59 || seconds > 59) {
      return "Invalid time values (HH: 00-23, mm:ss: 00-59)";
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const newErrors: typeof errors = {};

    if (!subject.trim()) newErrors.subject = "Subject is required";
    if (!call_date) {
      newErrors.call_date = "Call date & time is required";
    } else {
      const selectedDate = new Date(call_date);

      // Adjust to IST timezone
      const now = new Date();
      now.setMinutes(now.getMinutes() + now.getTimezoneOffset() + 330);

      // Truncate to minute precision
      selectedDate.setSeconds(0, 0);
      now.setSeconds(0, 0);

      if (selectedDate < now) {
        newErrors.call_date =
          "This date or time is expired. Please select a current or future date and time.";
      }
    }

    const durationError = validateDuration(duration);
    if (durationError) newErrors.duration = durationError;
    if (!status1) newErrors.status1 = "Status1 is required";
    if (!status2) newErrors.status2 = "Status2 is required";
    if (!call_description.trim()) newErrors.call_description = "Call description is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("lead_id", lead_id.toString());
    formData.append("subject", subject);
    const dateObj = new Date(call_date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = "00";
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    formData.append("call_date", formattedDate);
    formData.append("duration", duration);
    formData.append("status_1", status1);
    formData.append("status_2", status2);
    formData.append("call_description", call_description);
    formData.append("assigned_to", assigned_to);

    if (!token) {
      setErrors({ ...newErrors, assigned_to: "Authentication error! Please log in." });
      setSaving(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/call/add`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setSubject("");
        setCallDate("");
        setDuration("");
        setStatus1("");
        setStatus2("");
        setCallDescription("");
        setAssignedTo("");
        onClose();
        setTimeout(() => {
          setSuccessModalOpen(false);
          if (onCallAdded) onCallAdded();
        }, 2000);
      } else {
        setErrors({ ...newErrors, assigned_to: "Unexpected server response" });
      }
    } catch (error: any) {
      console.error("API Error:", error);
      setErrors({
        ...newErrors,
        assigned_to: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className={modalStyles}>
        <h4 className={modalTitle}>Schedule Call</h4>
        <p className={modalDescription}>
          Fill in the details to schedule a new call.
        </p>
        <form onSubmit={handleSubmit} noValidate className={formStyles}>
          <div className="relative">
            <label className={label}>
              Subject <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => handleChange(e, "subject")}
              className={`${input} ${errors.subject
                ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                : ""
                }`}
            />
            {errors.subject && <p className={errorMessage}>{errors.subject}</p>}
          </div>
          {isAdmin ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="relative">
                <label className={label}>
                  Call Date & Time <span className="text-error-500 dark:text-red-400">*</span>
                </label>
                <div className="relative">
                  <Input
                    type="datetime-local"
                    value={call_date}
                    onChange={(e) => handleChange(e, "call_date")}
                    min={getCurrentDateTime()}
                    max="2099-12-31T23:59"
                    className={`${input} ${errors.call_date
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                      }`}
                  />
                  <CalendarDays
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-white w-5 h-5 pointer-events-none"
                  />
                </div>

                {errors.call_date && <p className={errorMessage}>{errors.call_date}</p>}
              </div>
              <div className="relative">
                <ListboxComponent
                  value={users.find((user) => user.id === assigned_to) ?? null}
                  onChange={handleDropdownChange("assigned_to")}
                  items={users}
                  label="Assigned To"
                  displayKey="name"
                  className={`${input} ${errors.assigned_to
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                    }`}
                />
                {errors.assigned_to && (
                  <p className={errorMessage}>{errors.assigned_to}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="relative">
                <label className={label}>
                  Call Date & Time <span className="text-error-500 dark:text-red-400">*</span>
                </label>
                <Input
                  type="datetime-local"
                  value={call_date}
                  onChange={(e) => handleChange(e, "call_date")}
                  min={getCurrentDateTime()}
                  className={`${input} ${errors.call_date
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                    }`}
                />
                {errors.call_date && <p className={errorMessage}>{errors.call_date}</p>}
              </div>
              <div className="relative">
                <label className={label}>
                  Duration (HH:mm:ss) <span className="text-error-500 dark:text-red-400">*</span>
                </label>
                <InputMask
                  mask="99:99:99"
                  value={duration}
                  onChange={(e) => handleChange(e, "duration")}
                  placeholder="HH:mm:ss"
                  className={`${input} ${errors.duration
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                    }`}
                />
                {errors.duration && <p className={errorMessage}>{errors.duration}</p>}
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="relative">
              <label className={label}>
                Duration (HH:mm:ss) <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <InputMask
                mask="99:99:99"
                value={duration}
                onChange={(e) => handleChange(e, "duration")}
                placeholder="HH:mm:ss"
                className={`${input} ${errors.duration
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
                  }`}
              />
              {errors.duration && <p className={errorMessage}>{errors.duration}</p>}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <ListboxComponent
                value={status1Options.find((option) => option.id === status1) ?? null}
                onChange={handleDropdownChange("status1")}
                items={status1Options}
                label="Status1"
                error={errors.status1}
                displayKey="name"
                required
                className={`${input} ${errors.status1
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
                  }`}
              />
            </div>
            <div className="relative">
              <ListboxComponent
                value={status2Options.find((option) => option.id === status2) ?? null}
                onChange={handleDropdownChange("status2")}
                items={status2Options}
                label="Status2"
                error={errors.status2}
                displayKey="name"
                required
                className={`${input} ${errors.status2
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
                  }`}
              />
            </div>
          </div>
          <div className="relative">
            <label className={label}>
              Call Description <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={call_description}
              onChange={(e) => handleChange(e, "call_description")}
              rows={3}
              className={`${input} min-h-[5rem] ${errors.call_description
                ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                : ""
                }`}
            />
            {errors.call_description && (
              <p className={errorMessage}>{errors.call_description}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <div className="flex-1 flex gap-4 justify-center sm:justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`${saveButton} ${saving ? saveButtonDisabled : ""}`}
              >
                {saving ? "Saving..." : "Submit"}
              </button>
              <button type="button" onClick={onClose} className={closeButton}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className={successModalStyles}
      >
        <button
          onClick={() => setSuccessModalOpen(false)}
          className={successModalCloseButton}
        />
        <div className="flex justify-center">
          <div className={successModalIconContainer}>
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className={successModalHeading}>Call Created Successfully</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The call has been added to the system successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className={successModalButton}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default CallModal;